import {isNil} from 'ramda';

import {Nullish} from 'shared/web';

import {calculatePriceExclVat} from '../../../utils/calculatePriceExclVat';

interface BuildPriceBreakdownArgs {
  /** Gross price converted to the preview currency. */
  price: number | Nullish;
  currency: string;
  /** Gross price as the ad states it. */
  originalPrice: number | Nullish;
  originalCurrency: string | Nullish;
  vatRate: number | Nullish;
  isVatDeductible: boolean | Nullish;
}

/**
 * Prices the preview shows: the gross price, the net price when VAT can be deducted, and next to
 * each the amount in the currency of the ad - but only when that currency differs, otherwise the
 * same number would be printed twice.
 */
export function buildPriceBreakdown(args: BuildPriceBreakdownArgs) {
  if (isNil(args.price)) {
    return null;
  }

  const originalCurrency =
    !isNil(args.originalPrice) && args.originalCurrency !== args.currency
      ? args.originalCurrency
      : null;

  const toOriginal = (amount: number | Nullish) =>
    isNil(originalCurrency) || isNil(amount) ? null : {amount, currency: originalCurrency};

  const netPrice = args.isVatDeductible ? calculatePriceExclVat(args.price, args.vatRate) : null;

  return {
    withVat: {
      amount: args.price,
      currency: args.currency,
      original: toOriginal(args.originalPrice),
    },
    withoutVat: isNil(netPrice)
      ? null
      : {
          amount: netPrice,
          currency: args.currency,
          original: toOriginal(calculatePriceExclVat(args.originalPrice, args.vatRate)),
        },
  };
}
